// src/components/ecommerce/FeaturedManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Settings, Grid, List, Star, Plus, X, Search,
  Filter, Eye, EyeOff, Edit, ArrowUp, ArrowDown, Shuffle
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { ProductCard } from './ProductCard';
import toast from 'react-hot-toast';

export const FeaturedManager = ({ storeId, bookData, onUpdate }) => {
  const [featuredSettings, setFeaturedSettings] = useState({
    enabled: true,
    title: 'Featured Products',
    subtitle: 'Check out our top picks',
    displayCount: 8,
    layout: 'grid', // grid, carousel, list
    showFilters: true,
    autoRotate: false,
    rotateInterval: 10000,
    sortBy: 'manual' // manual, sales, rating, newest, price
  });

  const [allProducts, setAllProducts] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddProducts, setShowAddProducts] = useState(false);

  useEffect(() => {
    loadData();
  }, [storeId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [products, settings] = await Promise.all([
        ecommerceService.getProducts(storeId),
        ecommerceService.getFeaturedSettings(storeId)
      ]);

      setAllProducts(products || []);
      setFeaturedProducts(products?.filter(p => p.featured) || []);
      setAvailableProducts(products?.filter(p => !p.featured) || []);
      
      if (settings) {
        setFeaturedSettings(settings);
      }
    } catch (error) {
      console.error('Error loading featured data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveFeaturedSettings = async (settings = featuredSettings) => {
    try {
      await ecommerceService.updateFeaturedSettings(storeId, settings);
      setFeaturedSettings(settings);
      onUpdate?.({ featured: settings });
      toast.success('Featured section updated successfully');
    } catch (error) {
      console.error('Error saving featured settings:', error);
      toast.error('Failed to update featured section');
    }
  };

  const toggleProductFeatured = async (productId, featured) => {
    try {
      await ecommerceService.updateProduct(storeId, productId, { featured });
      
      const product = allProducts.find(p => p.id === productId);
      if (featured) {
        setFeaturedProducts(prev => [...prev, { ...product, featured: true }]);
        setAvailableProducts(prev => prev.filter(p => p.id !== productId));
      } else {
        setFeaturedProducts(prev => prev.filter(p => p.id !== productId));
        setAvailableProducts(prev => [...prev, { ...product, featured: false }]);
      }
      
      toast.success(featured ? 'Product added to featured' : 'Product removed from featured');
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Failed to update product');
    }
  };

  const reorderFeaturedProducts = async (fromIndex, toIndex) => {
    const reordered = [...featuredProducts];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    
    setFeaturedProducts(reordered);
    
    // Update order in database
    try {
      const updates = reordered.map((product, index) => ({
        id: product.id,
        featuredOrder: index
      }));
      await ecommerceService.updateProductsOrder(storeId, updates);
      toast.success('Product order updated');
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
  };

  const filteredAvailable = availableProducts.filter(product =>
    product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-blue-600" />
            Featured Products Manager
          </h2>
          <p className="text-gray-600">Manage your store's highlighted products section</p>
        </div>
        <button
          onClick={() => setShowAddProducts(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Add Featured Products
        </button>
      </div>

      {/* Settings */}
      <div className="bg-white border rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Featured Section Settings
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Section Title</label>
            <input
              type="text"
              value={featuredSettings.title}
              onChange={(e) => {
                const updated = { ...featuredSettings, title: e.target.value };
                setFeaturedSettings(updated);
                saveFeaturedSettings(updated);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle</label>
            <input
              type="text"
              value={featuredSettings.subtitle}
              onChange={(e) => {
                const updated = { ...featuredSettings, subtitle: e.target.value };
                setFeaturedSettings(updated);
                saveFeaturedSettings(updated);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Display Count</label>
            <input
              type="number"
              value={featuredSettings.displayCount}
              onChange={(e) => {
                const updated = { ...featuredSettings, displayCount: parseInt(e.target.value) };
                setFeaturedSettings(updated);
                saveFeaturedSettings(updated);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              min="1"
              max="20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Layout</label>
            <select
              value={featuredSettings.layout}
              onChange={(e) => {
                const updated = { ...featuredSettings, layout: e.target.value };
                setFeaturedSettings(updated);
                saveFeaturedSettings(updated);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="grid">Grid</option>
              <option value="carousel">Carousel</option>
              <option value="list">List</option>
            </select>
          </div>
        </div>
      </div>

      {/* Featured Products List */}
      <div className="bg-white border rounded-lg">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            Featured Products ({featuredProducts.length})
          </h3>
        </div>
        
        <div className="p-6">
          {featuredProducts.length === 0 ? (
            <div className="text-center py-12">
              <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Featured Products</h3>
              <p className="text-gray-600 mb-4">Add products to your featured section to highlight them on your store.</p>
              <button
                onClick={() => setShowAddProducts(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Add Featured Products
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {featuredProducts.map((product, index) => (
                <div key={product.id} className="border rounded-lg p-4 relative group">
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-1">
                      {index > 0 && (
                        <button
                          onClick={() => reorderFeaturedProducts(index, index - 1)}
                          className="p-1 bg-white rounded shadow-sm text-gray-600 hover:text-blue-600"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </button>
                      )}
                      {index < featuredProducts.length - 1 && (
                        <button
                          onClick={() => reorderFeaturedProducts(index, index + 1)}
                          className="p-1 bg-white rounded shadow-sm text-gray-600 hover:text-blue-600"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        onClick={() => toggleProductFeatured(product.id, false)}
                        className="p-1 bg-white rounded shadow-sm text-gray-600 hover:text-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="aspect-square bg-gray-100 rounded-lg mb-3 overflow-hidden">
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0].url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        No Image
                      </div>
                    )}
                  </div>
                  
                  <h4 className="font-medium text-sm truncate">{product.name}</h4>
                  <p className="text-lg font-bold text-gray-900 mt-1">
                    R{product.price?.toFixed(2) || '0.00'}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-500">Order: #{index + 1}</span>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                      Featured
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Products Modal */}
      {showAddProducts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold">Add Featured Products</h3>
              <button 
                onClick={() => setShowAddProducts(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 border-b">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search products..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredAvailable.map((product) => (
                  <div key={product.id} className="border rounded-lg p-4 hover:border-blue-300 transition-colors">
                    <div className="aspect-square bg-gray-100 rounded-lg mb-3 overflow-hidden">
                      {product.images?.[0] ? (
                        <img
                          src={product.images[0].url}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          No Image
                        </div>
                      )}
                    </div>
                    
                    <h4 className="font-medium text-sm truncate mb-1">{product.name}</h4>
                    <p className="text-lg font-bold text-gray-900 mb-3">
                      R{product.price?.toFixed(2) || '0.00'}
                    </p>
                    
                    <button
                      onClick={() => toggleProductFeatured(product.id, true)}
                      className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    >
                      Add to Featured
                    </button>
                  </div>
                ))}
              </div>
              
              {filteredAvailable.length === 0 && (
                <div className="text-center py-12">
                  <Search className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Products Found</h3>
                  <p className="text-gray-600">Try adjusting your search terms or add more products to your store.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};